import React, { useState, useEffect, useMemo } from 'react';
import { KnowledgeCards, type ConversationStage } from './KnowledgeCards';
import { AccountReadiness } from './AccountReadiness';
import { cn } from '@/lib/utils';
import type { Message } from './ChatInterface';

const PANEL_STATE_KEY = 'chatSidePanelState';

type PanelState = 'expanded' | 'minimized' | 'hidden';

interface ChatSidePanelProps {
  messages: Message[];
  className?: string;
}

/**
 * Derives the conversation stage from the message history.
 * Scans messages in reverse to find the most recent stage indicator.
 */
function deriveConversationStage(messages: Message[]): {
  stage: ConversationStage;
  platforms?: string[];
  objective?: string;
} {
  // Walk messages in reverse to find latest stage
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role !== 'assistant') continue;
    const content = msg.content;

    // Published
    if (content.published_campaign_results) {
      return { stage: 'published' };
    }

    // Proposal
    if (content.campaign_proposal) {
      // Try to extract platforms from proposal
      const proposal = content.campaign_proposal;
      const platforms: string[] = [];
      if (proposal.campaigns && Array.isArray(proposal.campaigns)) {
        for (const c of proposal.campaigns) {
          if (c.platform && !platforms.includes(c.platform)) {
            platforms.push(c.platform);
          }
        }
      }
      return { stage: 'proposal', platforms };
    }

    // Strategy
    if (content.campaign_strategy) {
      const strategy = content.campaign_strategy;
      const platforms = strategy.platforms?.map((p: any) => p.platform).filter(Boolean) || [];
      const objective = strategy.campaign_objective;
      return { stage: 'strategy', platforms, objective };
    }

    // Response type hints
    if (content.response_type === 'campaign_strategy_confirmation') {
      return { stage: 'strategy' };
    }
    if (content.response_type === 'campaign_review') {
      return { stage: 'proposal' };
    }
  }

  return { stage: 'initial' };
}

export const ChatSidePanel: React.FC<ChatSidePanelProps> = ({ messages, className }) => {
  const [panelState, setPanelState] = useState<PanelState>(() => {
    try {
      const saved = localStorage.getItem(PANEL_STATE_KEY);
      if (saved === 'expanded' || saved === 'minimized' || saved === 'hidden') return saved;
      return 'minimized'; // default
    } catch {
      return 'minimized';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(PANEL_STATE_KEY, panelState);
    } catch {
      // Ignore storage errors
    }
  }, [panelState]);

  const { stage, platforms, objective } = useMemo(
    () => deriveConversationStage(messages),
    [messages]
  );

  // Hidden: small pill button to reopen
  if (panelState === 'hidden') {
    return (
      <div className={cn('hidden xl:block fixed top-20 right-4 z-30', className)}>
        <button
          onClick={() => setPanelState('minimized')}
          className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-work-soft shadow-lg border border-work/30/60 hover:shadow-xl hover:scale-105 transition-all text-primary"
          title="Show campaign guide"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-[11px] font-medium">Guide</span>
        </button>
      </div>
    );
  }

  // Header bar (shared by minimized and expanded)
  const headerBar = (
    <div className="flex items-center justify-between px-4 py-3 sticky top-0 bg-work-soft/80 z-10 rounded-t-xl">
      <button
        onClick={() => setPanelState(panelState === 'minimized' ? 'expanded' : 'minimized')}
        className="flex items-center gap-2 flex-1 min-w-0"
        title={panelState === 'minimized' ? 'Expand panel' : 'Minimize panel'}
      >
        <svg className="w-3.5 h-3.5 text-work flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="text-xs font-semibold text-ink-2">Campaign Guide</h3>
        <svg
          className={cn(
            'w-3 h-3 text-ink-3 transition-transform duration-200 ml-auto flex-shrink-0',
            panelState === 'expanded' && 'rotate-180'
          )}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <button
        onClick={() => setPanelState('hidden')}
        className="p-1 rounded-md hover:bg-inset transition-colors ml-1 flex-shrink-0"
        title="Close panel"
      >
        <svg className="w-3.5 h-3.5 text-ink-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );

  return (
    <div className={cn(
      'hidden xl:block fixed top-16 right-4 z-30 w-[280px]',
      'bg-gradient-to-b from-blue-50/70 to-white rounded-xl shadow-xl border border-work/30/50',
      panelState === 'expanded' && [
        'max-h-[calc(100vh-5rem)]',
        'overflow-y-auto overscroll-contain',
        '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
      ],
      className
    )}>
      {headerBar}

      {/* Content - only shown when expanded */}
      {panelState === 'expanded' && (
        <div className="p-3 pt-0 space-y-5 border-t border-line">
          <div className="pt-3">
            <KnowledgeCards
              stage={stage}
              strategyPlatforms={platforms}
              strategyObjective={objective}
            />
          </div>

          <div className="border-t border-line pt-4">
            <AccountReadiness />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatSidePanel;
